import {
  ArrowLeft,
  Building2, Button, Calendar,
  Clock,
  MapPin,
  User,
  UserCheck
} from "@ews-admin/global-design-system";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { InlineLoader } from "../../components/atoms/InlineLoader";
import { AppointmentActions } from "../../components/molecules/AppointmentActions/AppointmentActions";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { PageHeader } from "../../components/molecules/PageHeader/PageHeader";
import { StatusBadge } from "../../components/molecules/StatusBadge/StatusBadge";
import useFacilityUser from "../../hooks/useFacilityUser";
import { appointmentFacilityName } from "../../utils/appointment-facility.utils";
import { appointmentApi } from "../../services/appointment.api";
import {
  setAppointmentToCancel,
  setShowCancelModal,
} from "../../store/reducers/appointmentSlice";
import { RootState } from "../../store/store";
import {
  Appointment,
  AppointmentStatusEnum,
} from "../../types/appointment.definition";

export const AppointmentDetails: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const { t, i18n } = useTranslation();
  const facilityUser = useFacilityUser();
  // Get cancel modal state from Redux
  const { showCancelModal, appointmentToCancel } = useSelector(
    (state: RootState) => state.appointments
  );

  const locale = useMemo(() => {
    return i18n.language.startsWith("fr") ? "fr-FR" : "en-US";
  }, [i18n.language]);

  useEffect(() => {
    const fetchAppointment = async () => {
      if (!appointmentId) {
        setIsLoading(false);
        return;
      }

      // Validate that appointmentId is a valid UUID format
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(appointmentId)) {
        setIsLoading(false);
        return;
      }

      try {
        const appointmentData = await appointmentApi.getAppointmentById(
          appointmentId
        );
        setAppointment(appointmentData);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAppointment();
  }, [appointmentId]);

  const handleStatusChange = async (
    appointmentIdParam: string,
    newStatus: string
  ) => {
    if (!appointment || !appointment.id) return;

    // Validate appointment ID is a valid UUID
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(appointmentIdParam)) {
      console.error("Invalid appointment ID format for status update");
      return;
    }

    try {
      setIsUpdating(true);
      await appointmentApi.updateAppointment(appointmentIdParam, {
        ...appointment,
        status: newStatus as Appointment["status"],
      });
      // Refresh the appointment data
      const appointmentData = await appointmentApi.getAppointmentById(
        appointmentIdParam
      );
      setAppointment(appointmentData);
    } catch (err) {
      console.error("Error updating appointment status:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle cancel confirmation
  const handleCancelConfirm = async () => {
    if (appointmentToCancel && appointmentToCancel.id) {
      try {
        setIsUpdating(true);
        await appointmentApi.cancelAppointment(appointmentToCancel.id);
        // Refresh the appointment data
        const refreshedAppointment = await appointmentApi.getAppointmentById(
          appointmentToCancel.id
        );
        setAppointment(refreshedAppointment);
        dispatch(setShowCancelModal(false));
        dispatch(setAppointmentToCancel(null));
      } catch (err) {
        console.error("Error cancelling appointment:", err);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  // Handle cancel modal close
  const handleCancelModalClose = () => {
    dispatch(setShowCancelModal(false));
    dispatch(setAppointmentToCancel(null));
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-6 min-h-screen">
        <InlineLoader />
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="p-6">
        <div className="py-12 text-center">
          <p className="text-lg font-semibold text-red-600">
            {t("appointment.details.notFound")}
          </p>
          <Button variant="ghost" onClick={handleBack} className="mt-4">
            <ArrowLeft className="mr-2 w-4 h-4" />
            {t("common.back")}
          </Button>
        </div>
      </div>
    );
  }

  const patientName = `${appointment.patient?.firstName || ""} ${appointment.patient?.lastName || ""
    }`.trim();

  const doctorName = `${appointment.doctor?.firstName || ""} ${appointment.doctor?.lastName || ""
    }`.trim();

  const bookedByName = appointment.bookedBy
    ? `${appointment.bookedBy.firstName || ""} ${appointment.bookedBy.lastName || ""
      }`.trim()
    : "";

  const appointmentDate = new Date(appointment.appointmentTime);
  const formattedDate = appointmentDate.toLocaleDateString(locale, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "Africa/Dakar",
  });
  const formattedTime = appointmentDate.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Africa/Dakar",
  });

  return (
    <div className="p-6 space-y-6 min-h-screen bg-neutral-50">
      <PageHeader
        title={t("appointment.details.title")}
        description={t("appointment.details.description")}
        icon={Calendar}
        actions={
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="mr-2 w-4 h-4" />
            {t("common.back")}
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Appointment Overview Card */}
          <div className="p-6 bg-white rounded-xl border shadow-sm border-neutral-200">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-neutral-900">
                  {patientName || `Patient ID: ${appointment.patientId}`}
                </h2>
                <div className="flex items-center mt-2 space-x-3">
                  <StatusBadge status={appointment.status} />
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full border ${appointment.appointmentType === "CLINIC"
                        ? "bg-blue-100 text-blue-800 border-blue-200"
                        : appointment.appointmentType === "ONLINE"
                          ? "bg-purple-100 text-purple-800 border-purple-200"
                          : "bg-gray-100 text-gray-800 border-gray-200"
                      }`}
                  >
                    {t(`appointment.manage.serviceTypes.${appointment.appointmentType}`, { defaultValue: appointment.appointmentType })}
                  </span>
                </div>
              </div>
              {appointment.id && (
                <div className="text-right">
                  <p className="text-xs text-neutral-500">
                    {t("appointment.details.id")}:
                  </p>
                  <p className="font-mono text-sm text-neutral-700">
                    {appointment.id}
                  </p>
                </div>
              )}
            </div>

            {/* Appointment Details Grid */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex items-start space-x-3">
                <Calendar className="flex-shrink-0 w-5 h-5 mt-0.5 text-neutral-400" />
                <div>
                  <p className="text-sm font-medium text-neutral-900">
                    {t("appointment.details.date")}
                  </p>
                  <p className="text-sm text-neutral-600">{formattedDate}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Clock className="flex-shrink-0 w-5 h-5 mt-0.5 text-neutral-400" />
                <div>
                  <p className="text-sm font-medium text-neutral-900">
                    {t("appointment.details.time")}
                  </p>
                  <p className="text-sm text-neutral-600">{formattedTime}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <User className="flex-shrink-0 w-5 h-5 mt-0.5 text-neutral-400" />
                <div>
                  <p className="text-sm font-medium text-neutral-900">
                    {t("appointment.details.doctor")}
                  </p>
                  <p className="text-sm text-neutral-600">
                    {doctorName || `Doctor ID: ${appointment.doctorId}`}
                  </p>
                </div>
              </div>

              {bookedByName && (
                <div className="flex items-start space-x-3">
                  <UserCheck className="flex-shrink-0 w-5 h-5 mt-0.5 text-neutral-400" />
                  <div>
                    <p className="text-sm font-medium text-neutral-900">
                      {t("appointment.details.bookedBy")}
                    </p>
                    <p className="text-sm text-neutral-600">{bookedByName}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start space-x-3">
                <Building2 className="flex-shrink-0 w-5 h-5 mt-0.5 text-neutral-400" />
                <div>
                  <p className="text-sm font-medium text-neutral-900">
                    {t("appointment.details.facility")}
                  </p>
                  <p className="text-sm text-neutral-600">
                    {appointmentFacilityName(appointment, facilityUser?.healthcareFacility) ??
                      appointment.healthcareFacilityId}
                  </p>
                </div>
              </div>

              {appointment.location && (
                <div className="flex items-start space-x-3">
                  <MapPin className="flex-shrink-0 w-5 h-5 mt-0.5 text-neutral-400" />
                  <div>
                    <p className="text-sm font-medium text-neutral-900">
                      {t("appointment.details.lieu")}
                    </p>
                    <p className="text-sm text-neutral-600">
                      {appointment.location.address || "Location not specified"}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Reason */}
            {appointment.reason && (
              <div className="pt-6 mt-6 border-t border-neutral-200">
                <p className="mb-2 text-sm font-medium text-neutral-900">
                  {t("appointment.details.reason")}
                </p>
                <p className="text-sm text-neutral-600">{appointment.reason}</p>
              </div>
            )}

            {/* Timestamps */}
            {(appointment.createdAt || appointment.updatedAt) && (
              <div className="pt-6 mt-6 border-t border-neutral-200">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {appointment.createdAt && (
                    <div>
                      <p className="text-xs text-neutral-500">
                        {t("appointment.details.createdAt")}
                      </p>
                      <p className="text-sm text-neutral-700">
                        {new Date(appointment.createdAt).toLocaleString(locale)}
                      </p>
                    </div>
                  )}
                  {appointment.updatedAt && (
                    <div>
                      <p className="text-xs text-neutral-500">
                        {t("appointment.details.lastUpdated")}
                      </p>
                      <p className="text-sm text-neutral-700">
                        {new Date(appointment.updatedAt).toLocaleString(locale)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Actions */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="p-6 bg-white rounded-xl border shadow-sm border-neutral-200">
            <h3 className="mb-4 text-lg font-semibold text-neutral-900">
              {t("appointment.quickActions.title")}
            </h3>
            <div className="space-y-2">
              {(() => {
                const appointmentTime = new Date(appointment.appointmentTime);
                const now = new Date();
                const isAppointmentInPast = appointmentTime < now;
                const hasActions =
                  isUpdating ||
                  (appointment.status === AppointmentStatusEnum.CONFIRMED &&
                    !isAppointmentInPast) ||
                  appointment.status === AppointmentStatusEnum.IN_PROGRESS;

                if (!hasActions || !appointment.id) return null;

                return (
                  <AppointmentActions
                    appointment={appointment}
                    status={appointment.status}
                    isUpdating={isUpdating}
                    onStatusChange={handleStatusChange}
                    fullWidth
                  />
                );
              })()}
              {appointment.patientId && (
                <Button
                  variant="ghost"
                  className="justify-start w-full"
                  onClick={() => navigate(`/patients/${appointment.patientId}`)}
                >
                  <User className="mr-2 w-4 h-4" />
                  {t("appointment.quickActions.viewPatientProfile")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCancelModal}
        title={t("appointment.cancelDialog.title")}
        message={t("appointment.cancelDialog.message", {
          firstName: appointmentToCancel?.patient?.firstName || "",
          lastName: appointmentToCancel?.patient?.lastName || "",
        })}
        confirmLabel={t("appointment.cancelDialog.confirm")}
        cancelLabel={t("appointment.cancelDialog.cancel")}
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelModalClose}
        variant="danger"
        isLoading={isUpdating && appointmentToCancel?.id === appointment?.id}
      />
    </div>
  );
};
